import { call, put, takeLatest } from 'redux-saga/effects';
import HeroUsersAPI from 'api-services/HeroUserApi';
import { isSuccessResponse } from 'api-services/types/guards';
import { processUserStatus } from '../utils/processUserStatus';
import { doneStartingSession } from '../sessions/actionCreators';
import { checkUserHelper } from '../utils/helperSagas';
import { getUserFlagsCall } from '../userFlags/saga';
import { doneLogin, errorLogin, doneGettingUserStatus, errorGettingUserStatus, doneGettingOwnerStatus, errorGettingOwnerStatus, doneFetchUserInfo, errorFetchUserInfo, doneDeletingUser, errorDeletingUser, selectHeroAccount } from './actionCreators';
import { LOGIN_USER, GET_USER_STATUS, DELETE_USER, GET_NEW_USER_STATUS, FETCH_USER_INFO } from './actionTypes';
import { logout } from '../auth/actionCreators';
function* login(constAccessParams, action) {
    const { username, password } = action.data;
    // Try to login user
    const result = yield call(HeroUsersAPI.getPrivateToken, constAccessParams, username, password);
    // Check result and broadcast it
    if (isSuccessResponse(result)) {
        // Append email and current time in milliseconds to result
        const resultExt = Object.assign(Object.assign({}, result.data), { email: username, obtain_time: new Date().getTime() });
        // Now fetch user status also
        const statusResult = yield call(HeroUsersAPI.userStatus, Object.assign(Object.assign({}, constAccessParams), resultExt));
        if (isSuccessResponse(statusResult)) {
            // If there is session in progress put it as current one
            if (statusResult.data.sessions.length &&
                statusResult.data.sessions[statusResult.data.sessions.length - 1].status !==
                    'release_candidate') {
                yield put(doneStartingSession(statusResult.data.sessions[statusResult.data.sessions.length - 1]));
            }
            yield put(doneLogin(Object.assign(Object.assign({}, resultExt), { status: Object.assign({}, statusResult.data) })));
        }
        else if (statusResult.statusCode === 4) {
            yield put(logout());
        }
        else {
            yield put(errorLogin(statusResult));
        }
    }
    else {
        yield put(errorLogin(result));
    }
}
export function* watchLoginCalls(constAccessParams) {
    yield takeLatest(LOGIN_USER, login, constAccessParams);
}
/* eslint-disable-next-line sonarjs/cognitive-complexity */
export function* userStatus(constAccessParams) {
    const newAccessParams = yield call(checkUserHelper, constAccessParams);
    const isBackofficeApp = !!constAccessParams.IS_BACKOFFICE_APP;
    let userStatusResult = yield call(HeroUsersAPI.userStatus, newAccessParams);
    // repeat user status call if account was not already selected
    if (isSuccessResponse(userStatusResult) &&
        !newAccessParams.selected_account_id &&
        !isBackofficeApp) {
        const { selected_account_id } = processUserStatus(userStatusResult.data);
        yield put(selectHeroAccount({ selected_account_id }));
        userStatusResult = yield call(HeroUsersAPI.userStatus, Object.assign(Object.assign({}, newAccessParams), { selected_account_id }));
    }
    // Broadcast result
    if (isSuccessResponse(userStatusResult)) {
        const { data: userStatus } = userStatusResult;
        // FIXME below logic (order of tests) is most likely wrong
        analytics &&
            (analytics === null || analytics === void 0 ? void 0 : analytics.identify(String(userStatus.user_id), {
                user_id: userStatus.user_id,
                role: userStatus.is_owner
                    ? 'is_owner'
                    : userStatus.is_read_only_owner
                        ? 'is_read_only_owner'
                        : userStatus.is_caregiver
                            ? 'is_caregiver'
                            : userStatus.is_read_only_caregiver
                                ? 'is_read_only_caregiver'
                                : 'unknown'
            }));
        yield put(doneGettingUserStatus(userStatus));
        if (!userStatus.is_owner && !isBackofficeApp) {
            yield call(ownerStatus, constAccessParams);
        }
    }
    else if (userStatusResult.statusCode === 4) {
        yield put(logout());
    }
    else {
        yield put(errorGettingUserStatus(Object.assign({}, userStatusResult)));
    }
    yield call(getUserFlagsCall, constAccessParams);
}
export function* watchUserStatusCalls(constAccessParams) {
    yield takeLatest(GET_USER_STATUS, userStatus, constAccessParams);
}
export function* ownerStatus(constAccessParams) {
    const newAccessParams = yield call(checkUserHelper, constAccessParams);
    const result = yield call(HeroUsersAPI.userStatus, newAccessParams, true);
    if (isSuccessResponse(result)) {
        yield put(doneGettingOwnerStatus(result.data));
    }
    else if (result.statusCode === 4) {
        yield put(logout());
    }
    else {
        yield put(errorGettingOwnerStatus(result));
    }
}
function* deleteAccount(constAccessParams) {
    // Check current access token
    const newAccessParams = yield call(checkUserHelper, constAccessParams);
    // Get user status
    const result = yield call(HeroUsersAPI.delete, newAccessParams);
    // Broadcast result
    if (isSuccessResponse(result)) {
        yield put(doneDeletingUser());
    }
    else {
        yield put(errorDeletingUser(result));
    }
}
export function* watchDeleteAccountCalls(constAccessParams) {
    yield takeLatest(DELETE_USER, deleteAccount, constAccessParams);
}
export function* fetchUserInfo(constAccessParams, action) {
    const { email } = action.data;
    // Check public API access token
    let token;
    const tokenResult = yield call(HeroUsersAPI.getPublicToken, constAccessParams);
    if (isSuccessResponse(tokenResult)) {
        token = tokenResult.data;
    }
    else {
        yield put(errorFetchUserInfo(tokenResult));
        return;
    }
    const result = yield call(HeroUsersAPI.fetchUserInfo, Object.assign(Object.assign({}, constAccessParams), token), email);
    if (isSuccessResponse(result)) {
        yield put(doneFetchUserInfo(result.data));
    }
    else if (result.statusCode === 1 && result.msg === 'no_email') {
        yield put(doneFetchUserInfo({
            email_exists: false
        }));
    }
    else {
        yield put(errorFetchUserInfo(result));
    }
}
function* getNewUserStatus(constAccessParams) {
    const user = yield call(checkUserHelper, constAccessParams);
    const userStatusResult = yield call(HeroUsersAPI.userStatus, user);
    if (isSuccessResponse(userStatusResult)) {
        yield put(doneLogin(Object.assign(Object.assign({}, user), { status: userStatusResult.data })));
    }
    else if (userStatusResult.statusCode === 4) {
        yield put(logout());
    }
    else {
        yield put(doneLogin(user));
    }
    yield call(getUserFlagsCall, constAccessParams);
}
export function* watchGetNewUserStatusCalls(constAccessParams) {
    yield takeLatest(GET_NEW_USER_STATUS, getNewUserStatus, constAccessParams);
}
export function* watchUserInfoCalls(constAccessParams) {
    // Fake for now
    yield takeLatest(FETCH_USER_INFO, fetchUserInfo, constAccessParams);
}
//# sourceMappingURL=saga.js.map